import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppThunkDispatch, RootState } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { toast } from "react-toastify";
import DocViewer, { IDocument } from "react-doc-viewer";
import CheckWhiteIcon from "@styles/all-images/icons/checkwhite.svg";
import CloseBlueIcon from "@styles/all-images/icons/closeblue.svg";
import CloseRedIcon from "@styles/all-images/icons/closered.svg";
import ApprovedIcon from "@styles/all-images/icons/checkgreen.svg";
import TierConfigurationRejectionReason from "@pages/TierConfiguration/TierConfigurationRejectionReason";
import OnboardingInputFields, {
  enumFields,
  InputFields,
} from "./OnboardingInputFeilds";
import { useNavigate } from "react-router-dom";
import DisplaySopDetails from "./DisplaySopDetails";
import DisplayRejectReason from "./DisplayRejectReason";
import { DocumentApprovalStatus } from "@/constants/enums/document-approval-status.enum";
import { OnboardingDocumentMasterType } from "@/constants/enums/onboarding-document-master-types.enum";
import DocViewerComponent from "@/components/common/DocViewer";
import ChatIconBlue from "@styles/all-images/icons/ChatIconBlue.svg";
import StandaloneChatDrawer, {
  StandaloneChatDrawerType,
} from "@/pages/LiveChat/StandaloneChatDrawer";
import { DocumentInfo } from "@/pages/LiveChat/StandaloneChatDrawer/ChatComponent";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import {
  DocumentsPermissions,
  SignPermissions,
} from "@/constants/permission-modules/base-permissions";
import { usePermissionState } from "@/providers/PermissionProvider";
interface DocumentDetail {
  id: number;
  created_by: number;
  document_name: string;
  document_master: number;
  document_type_id: OnboardingDocumentMasterType;
  file_name: string;
  document_url: string;
  rejection_reason?: string;
  rejected_at?: string;
  s3_key: string;
  size: number;
  status: DocumentApprovalStatus;
  status_text: string;
  rejected_by: string | null;
  action_by_name: string | null;
  action_at: string | null;
  created_at: string;
  updated_at: string;
  note: string;
  sop: any;
  input_fields?: InputFields;
  room_id?: number | null;
}

interface OnboardingDocumentDetailProps {
  isApprovedView?: boolean;
}

const OnboardingDocumentDetail = ({
  isApprovedView,
}: OnboardingDocumentDetailProps) => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isHistoryDocument = searchParams.get("source") === "history";
  const dispatch = useDispatch<AppThunkDispatch>();
  const [documentDetail, setDocumentDetail] = useState<DocumentDetail | null>(
    null,
  );
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState<IDocument[]>([]);
  const navigate = useNavigate();
  const userType = JSON.parse(localStorage.getItem("user") || "{}").user_type
    .id;
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isApproveDisabled, setIsApproveDisabled] = useState(true);
  const [documentChatUserId, setDocumentChatUserId] = useState<number | null>(
    null,
  );
  const [documentChatDocumentInfo, setDocumentChatDocumentInfo] =
    useState<DocumentInfo | null>(null);
  const [documentChatRoomId, setDocumentChatRoomId] = useState<string | null>(
    null,
  );
  const [isDocumentChatOpen, setIsDocumentChatOpen] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const [banks, setBanks] = useState<Array<{ id: number; name: string }>>([]);
  const { isPermitted } = usePermissionState();
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const fetchDocumentDetail = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const { authenticatedCall } = actionsHandler(
        isHistoryDocument ? "viewHistoryDocument" : "viewOnboardedDocument",
      );
      const response = await dispatch(
        authenticatedCall({
          method: "get",
          customParams: [`${id}/`],
        }),
      );
      if (isHistoryDocument) {
        setDocumentDetail(response.data.data);
      } else {
        setDocumentDetail(response.data.data);
        const hasInputFields = response.data.data.input_fields !== null;
        setIsApproveDisabled(hasInputFields);
        checkFormValidity(response.data.data);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Error fetching document:", error);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [id, isHistoryDocument, dispatch]);

  const checkFormValidity = (data: DocumentDetail) => {
    const inputFields =
      [...enumFields[data.document_type_id as OnboardingDocumentMasterType]] ||
      [];
    if (inputFields && inputFields.length > 0) {
      const requiredField = inputFields?.find((field: any) => field.required);
      const sopRequiredField = data?.sop?.data?.find(
        (field: any) => !!field.input_type,
      );
      const isInputRequiredValueAvailable =
        !requiredField ||
        data?.input_fields?.[requiredField?.name as keyof InputFields]
          ? true
          : false;
      const isSopRequiredValueAvailable =
        !sopRequiredField ||
        data?.input_fields?.[
          sopRequiredField?.input_type?.field as keyof InputFields
        ]
          ? true
          : false;
      if (isInputRequiredValueAvailable && isSopRequiredValueAvailable) {
        setIsFormValid(true);
      } else {
        setIsFormValid(false);
      }
    }
  };

  const fetchBanks = useCallback(async () => {
    try {
      const { authenticatedCall } = actionsHandler("getBanks");
      const response = await dispatch(
        authenticatedCall({
          method: "get",
        }),
      );

      if (response.ok) {
        setBanks(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching banks:", error);
    }
  }, [dispatch]);

  // Update form values when document details are loaded

  const handleSettingDocuments = async () => {
    if (!documentDetail?.document_url) {
      setDocuments([
        {
          uri: "No Document Available",
          fileType: "",
        },
      ]);
      return;
    }
    const documents: IDocument[] = [
      {
        uri: documentDetail.document_url,
        fileType: documentDetail.file_name.split(".").pop() || "",
      },
    ];
    setDocuments(documents);
  };

  useEffect(() => {
    if (documentDetail) {
      handleSettingDocuments();
    }
  }, [documentDetail]);
  useEffect(() => {
    fetchDocumentDetail();
  }, []);

  useEffect(() => {
    if (
      !(
        userType !== 3 &&
        documentDetail?.status !== DocumentApprovalStatus.pending &&
        !isPermitted(SignPermissions.VIEW_SIGN)
      ) &&
      documentDetail?.document_type_id === OnboardingDocumentMasterType.finOb11
    ) {
      fetchBanks();
    }
  }, [fetchBanks, documentDetail]);

  // Handle document approval/rejection
  const handleReject = () => {
    setIsRejectionModalOpen(true);
  };

  const handleRejectSubmit = async (rejectionReason: string) => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("documentApproval");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            document_id: id,
            status: 3,
            note: rejectionReason,
          },
        }),
      );

      if (response.ok) {
        toast.success(response.data.message);
        fetchDocumentDetail();
        setIsRejectionModalOpen(false);
        navigate(-1);
      }
    } catch (error) {
      console.error("Error rejecting document:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      setIsLoading(true);
      const { authenticatedCall } = actionsHandler("documentApproval");
      const response = await dispatch(
        authenticatedCall({
          method: "post",
          payload: {
            document_id: id,
            status: 2,
          },
        }),
      );

      if (response.ok) {
        toast.success(response.data.message);
        fetchDocumentDetail();
        navigate(-1);
      }
    } catch (error) {
      console.error("Error approving document:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChat = () => {
    if (!documentDetail?.id) {
      return;
    }
    const userId =
      user?.user_type?.id === UserTierType.Tier4
        ? user?.id
        : documentDetail?.created_by;
    setDocumentChatUserId(userId);
    setDocumentChatDocumentInfo({
      id: documentDetail?.id,
      title: documentDetail?.document_name,
      status: documentDetail?.status,
    });
    setDocumentChatRoomId(documentDetail?.room_id?.toString() || null);
    setIsDocumentChatOpen(true);
  };

  useEffect(() => {
    if (!isDocumentChatOpen) {
      setDocumentChatRoomId(null);
      setDocumentChatUserId(null);
    }
  }, [isDocumentChatOpen]);

  const handleInputFieldsSave = useCallback(() => {
    fetchDocumentDetail();
  }, [fetchDocumentDetail]);

  if (isLoading || isRefreshing) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-h-screen">
        <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-gray-900" />
      </div>
    );
  }

  return (
    <div className="tw-relative">
      <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
        <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b tw-border-[#E9EAEB]">
          <div className="tw-flex tw-justify-between tw-items-center">
            <div className="tw-flex tw-items-center">
              <a onClick={() => navigate(-1)}>
                <img src={BackIcon} alt="Back Icon" />
              </a>
              <h1 className="tw-text-[22px] tw-ml-4 tw-font-semibold tw-text-[#232323]">
                {documentDetail?.document_name || documentDetail?.file_name}
              </h1>
            </div>
            {!isApprovedView && (
              <>
                {!isHistoryDocument &&
                  userType === UserTierType.Tier3 &&
                  documentDetail?.status === DocumentApprovalStatus.pending &&
                  isPermitted(
                    DocumentsPermissions.APPROVE_REJECT_DOCUMENTS_TO_BE_APPROVED,
                  ) && (
                    <div className="tw-flex tw-space-x-4 tw-mt-4">
                      <button onClick={handleChat}>
                        <img src={ChatIconBlue} alt="chat Icon" />
                      </button>
                      <button
                        onClick={() => setIsRejectionModalOpen(true)}
                        className="tw-flex-1 tw-px-4 tw-py-2 tw-border tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-semibold tw-border-[#146C94] tw-rounded-lg tw-text-[#146C94]"
                        disabled={isLoading}
                      >
                        Reject
                        <img
                          src={CloseBlueIcon}
                          alt="Close Icon"
                          className="tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6 tw-max-w-6 tw-max-h-6"
                        />
                      </button>
                      <button
                        onClick={handleApprove}
                        className={`tw-flex tw-items-center tw-justify-center tw-gap-2 tw-font-semibold tw-text-white tw-rounded-lg tw-px-4 tw-py-2 tw-flex-1 ${
                          !isFormValid || isLoading
                            ? "tw-bg-[#146C94] tw-opacity-50 tw-cursor-not-allowed"
                            : "tw-bg-[#146C94] hover:tw-bg-[#105C84]"
                        }`}
                        disabled={!isFormValid || isLoading}
                      >
                        {isLoading ? "Processing..." : "Approve"}
                        <img
                          src={CheckWhiteIcon}
                          alt="Check Icon"
                          className="tw-w-6 tw-h-6 tw-min-w-6 tw-min-h-6 tw-max-w-6 tw-max-h-6"
                        />
                      </button>
                    </div>
                  )}
                {!isHistoryDocument &&
                  userType === UserTierType.Tier3 &&
                  documentDetail?.status ===
                    DocumentApprovalStatus.rejected && (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div onClick={handleChat}>
                        <img src={ChatIconBlue} alt="chat Icon" />
                      </div>
                      <div className="tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-rounded-full tw-border tw-border-[#E34234] tw-bg-[#E342341A] tw-text-[#E34234] tw-font-medium">
                        <span>Rejected</span>
                        <img
                          src={CloseRedIcon}
                          alt="Rejected Icon"
                          className="tw-w-4 tw-h-4 tw-ml-2"
                        />
                      </div>
                    </div>
                  )}
                {!isHistoryDocument &&
                  userType === UserTierType.Tier3 &&
                  documentDetail?.status ===
                    DocumentApprovalStatus.approved && (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div onClick={handleChat}>
                        <img src={ChatIconBlue} alt="chat Icon" />
                      </div>
                      <div className="tw-flex tw-items-center tw-justify-center tw-px-4 tw-py-2 tw-rounded-full tw-border tw-border-[#2E8B57] tw-bg-[#8A9A5B33] tw-text-[#2E8B57] tw-font-medium">
                        <span>Approved</span>
                        <img
                          src={ApprovedIcon}
                          alt="Approved Icon"
                          className="tw-w-4 tw-h-4 tw-ml-2"
                        />
                      </div>
                    </div>
                  )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="tw-p-6 tw-mt-[177px] tw-gap-6 tw-flex-col tw-flex">
        <div className="tw-flex tw-items-start tw-gap-6">
          <div className="tw-w-[20%] tw-flex tw-flex-col tw-gap-6">
            {documentDetail?.sop?.data?.length > 0 && (
              <DisplaySopDetails
                title="SOP"
                items={documentDetail?.sop}
                readOnly={
                  userType !== 3 ||
                  documentDetail?.status !== DocumentApprovalStatus.pending
                }
                initialValues={documentDetail?.input_fields}
                onSave={handleInputFieldsSave}
              />
            )}
            <DisplayRejectReason
              rejectedBy={
                documentDetail?.rejected_by ||
                documentDetail?.action_by_name ||
                null
              }
              reason={documentDetail?.note || ""}
              updatedAt={
                documentDetail?.updated_at || documentDetail?.action_at || ""
              }
            />
          </div>

          <div className="tw-w-[60%] tw-flex tw-flex-col tw-gap-6">
            {/* {documentDetail?.document_url ? ( */}
            <div className="tw-bg-white tw-p-4 tw-rounded-lg tw-shadow-md tw-border tw-border-[#E9EAEB]">
              <DocViewerComponent documents={documents} />
            </div>
          </div>

          <div className="tw-w-[20%] tw-flex tw-flex-col tw-gap-6">
            {documentDetail?.document_type_id && (
              <OnboardingInputFields
                document_master={documentDetail.document_type_id}
                input_fields={documentDetail.input_fields || {}}
                inputFieldCustomOptions={{
                  bank: banks.map((bank) => ({
                    id: bank.id,
                    label: bank.name,
                    value: bank.id,
                  })),
                }}
                readOnly={
                  userType !== 3 ||
                  documentDetail?.status !== DocumentApprovalStatus.pending ||
                  !isPermitted(SignPermissions.VIEW_SIGN)
                }
                onSave={handleInputFieldsSave}
              />
            )}
          </div>
        </div>
      </div>

      {isRejectionModalOpen && (
        <TierConfigurationRejectionReason
          onClose={() => setIsRejectionModalOpen(false)}
          onSubmit={handleRejectSubmit}
          isLoading={isLoading}
        />
      )}

      {documentChatUserId && (
        <StandaloneChatDrawer
          isChatNotificationOpen={isDocumentChatOpen}
          setIsChatNotificationOpen={setIsDocumentChatOpen}
          documentInfo={documentChatDocumentInfo || undefined}
          documentId={documentChatDocumentInfo?.id || undefined}
          userId={documentChatUserId}
          customChatRoomId={documentChatRoomId}
          type={StandaloneChatDrawerType.DOCUMENT}
        />
      )}
    </div>
  );
};

export default OnboardingDocumentDetail;
